import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/use-toast';

// icons
import { Eye, EyeSlash, User, Lock } from 'iconsax-react';

// project imports
import { useAuth } from '../../hooks/useAuth';

// validation schema
const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().required('Password is required')
});

// ==============================|| LOGIN PAGE ||============================== //

const Login = () => {
  const navigate = useNavigate();
  const { login, isLoading, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Clean up any orphaned forgot password flow state
    sessionStorage.removeItem('forgot_email');
  }, []);

  const handleSubmit = async (values) => {
    try {
      clearError();
      await login(values);
      toast({
        title: "Login Successful",
        description: "Welcome back to OptiHHCP!",
      });
      navigate('/dashboard');
    } catch (err) {
      toast({
        title: "Login Failed",
        description: err.response?.data?.message || 'Login failed. Please check your credentials.',
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-[400px] border-border shadow-lg">
        <CardContent className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
              OptiHHCP Admin
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Sign in to your admin account
            </p>
          </div>

          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <Form className="space-y-4">
                {/* Email field */}
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                      <User size={20} />
                    </div>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="admin@optihhcp.com"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`pl-10 ${
                        touched.email && errors.email ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                  </div>
                  {touched.email && errors.email && (
                    <p className="text-xs text-destructive">{errors.email}</p>
                  )}
                </div>

                {/* Password field */}
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-xs font-semibold text-primary hover:underline hover:text-primary/90 transition-all"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                      <Lock size={20} />
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`pl-10 pr-10 ${
                        touched.password && errors.password ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <p className="text-xs text-destructive">{errors.password}</p>
                  )}
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 shadow-md transition-all duration-200 mt-2 flex items-center justify-center gap-2"
                >
                  {isLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;